import { ArrowLeft, Zap, AlertTriangle, CheckCircle2 } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Head } from "vite-react-ssg";
import { Card, CardContent } from "@/components/ui/card";
import RelatedLinks from "@/components/RelatedLinks";

const DeepseekV41FlashBeta = () => {
  const navigate = useNavigate();
  const canonical = "https://deepseek.ai/blog/deepseek-v41-flash-beta-native-multimodal";

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline:
      "DeepSeek V4.1 Flash Enters a Two-Day API Beta: New Architecture, Native Multimodal, 20 Concurrent Requests",
    datePublished: "2026-09-08T16:00:00+00:00",
    dateModified: "2026-09-08T16:00:00+00:00",
    author: {
      "@type": "Organization",
      name: "The Independent DeepSeek Guide",
      url: "https://deepseek.ai",
    },
    publisher: {
      "@type": "Organization",
      name: "The Independent DeepSeek Guide",
      logo: { "@type": "ImageObject", url: "https://deepseek.ai/og-image.png" },
    },
    description:
      "On September 8, 2026 DeepSeek opened limited testing of deepseek-v4.1-flash-expires-on-0910: a new architecture with native multimodal input, V4-Flash pricing and a 20-concurrent-request cap per account. What is confirmed, what is not, and how to test it before the model ID expires.",
    mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://deepseek.ai/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://deepseek.ai/blog" },
      { "@type": "ListItem", position: 3, name: "DeepSeek V4.1 Flash beta", item: canonical },
    ],
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "What is the DeepSeek V4.1 Flash model name?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "The test model ID is deepseek-v4.1-flash-expires-on-0910. The base URL stays https://api.deepseek.com — only the model string changes. The trailing 0910 is the expiry: the ID stops serving after September 10, 2026.",
        },
      },
      {
        "@type": "Question",
        name: "Is DeepSeek V4.1 Flash an official release?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "No. DeepSeek described it in its official community groups as an intermediate version (中间版本) opened for limited testing inside the live API. There is no model card, no technical report and no changelog entry, and the public Models & Pricing page still lists only DeepSeek V4 Flash, DeepSeek V4 Pro and DeepSeek-V4-Flash-Vision-Exp.",
        },
      },
      {
        "@type": "Question",
        name: "How much does DeepSeek V4.1 Flash cost?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "During the beta, billing matched deepseek-v4-flash at the flat September 2026 rates: $0.14 per 1M input tokens on a cache miss, $0.0028 on a cache hit and $0.28 per 1M output tokens. Those rates were superseded when DeepSeek-V4.1-Flash went generally available on September 9, 2026 and a peak/off-peak rate card took effect.",
        },
      },
      {
        "@type": "Question",
        name: "What are the rate limits on the V4.1 Flash beta?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Each account is capped at 20 concurrent requests during the test. That cap is the practical constraint on benchmarking: throughput measurements taken above it reflect queueing, not model speed.",
        },
      },
      {
        "@type": "Question",
        name: "Does V4.1 Flash replace DeepSeek-V4-Flash-Vision-Exp?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Not yet, but that is the direction. Vision-Exp is a separate experimental vision model layered onto the Flash line; V4.1 Flash is described as handling image and text input natively inside one architecture, which removes the separate encoder step.",
        },
      },
      {
        "@type": "Question",
        name: "Should I put the V4.1 Flash beta in production?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "No. The expiry date is written into the model ID, so any production call pinned to deepseek-v4.1-flash-expires-on-0910 breaks after September 10, 2026. Keep production on deepseek-v4-flash and use the beta ID only in a test path.",
        },
      },
    ],
  };

  return (
    <>
      <Head>
        <title>DeepSeek V4.1 Flash Beta: Native Multimodal, 2-Day Window</title>
        <meta
          name="description"
          content="September 8, 2026: DeepSeek opened deepseek-v4.1-flash-expires-on-0910 for limited testing — new architecture, native multimodal input, V4-Flash pricing, 20 concurrent requests, expiring September 10."
        />
        <link rel="canonical" href={canonical} />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        <meta
          property="og:title"
          content="DeepSeek V4.1 Flash Enters a Two-Day API Beta with Native Multimodal"
        />
        <meta
          property="og:description"
          content="New architecture, images and text in one model, same V4-Flash billing, 20 concurrent requests per account — and a model ID that expires on September 10, 2026."
        />
        <meta property="og:image" content="https://deepseek.ai/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Head>

      <div className="min-h-screen bg-white">
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Button
            variant="ghost"
            onClick={() => navigate("/blog")}
            className="mb-6 text-slate-600 hover:text-[#0066FF]"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Blog
          </Button>

          <nav aria-label="Breadcrumb" className="text-xs text-slate-500 mb-4">
            <Link to="/" className="hover:text-[#0066FF]">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/blog" className="hover:text-[#0066FF]">Blog</Link>
            <span className="mx-2">/</span>
            <span className="text-slate-700">DeepSeek V4.1 Flash beta</span>
          </nav>

          <div className="mb-6 inline-flex items-center gap-2 text-xs font-medium bg-blue-50 text-[#0066FF] border border-blue-200 px-3 py-1.5 rounded-full">
            <Zap className="h-3.5 w-3.5" />
            API changelog · September 8, 2026 · Independent Guide
          </div>

          <article>
            <h1 className="text-3xl md:text-5xl font-bold text-slate-900 mb-4 leading-tight">
              DeepSeek V4.1 Flash Enters a Two-Day API Beta: New Architecture, Native Multimodal, and
              an Expiry Date in the Model Name
            </h1>

            <p className="text-lg text-slate-600 mb-2">
              By the Deep Seek AI editorial desk · September 8, 2026 · 6 min read
            </p>
            <p className="text-base text-slate-500 mb-8 italic">
              DeepSeek did what it usually does before an announcement: it dropped the model into the
              live API. Around 15:00 Beijing time on September 8, 2026, the team told its official
              community groups that an "intermediate version" — <code>deepseek-v4.1-flash-expires-on-0910</code>{" "}
              — was open for limited testing. The trailing <code>0910</code> is the whole story: this
              build is scheduled to go offline around September 10.
            </p>

            <Card className="mb-8 border-primary/40 bg-primary/5">
              <CardContent className="p-6">
                <h2 className="text-lg font-bold text-slate-900 mb-2 mt-0">
                  Update, September 18, 2026: this beta is over — V4.1-Flash is now official
                </h2>
                <p className="text-sm text-slate-700 mb-0">
                  DeepSeek released <strong>DeepSeek-V4.1-Flash</strong> as a general-availability
                  model on <strong>September 9, 2026</strong> under the API name{" "}
                  <code>deepseek-flash</code>. The expiring test ID described below no longer serves
                  traffic, V4-Flash and V4-Flash-Vision-Exp are retired, and new peak/off-peak rates
                  apply. The article below is kept as a record of the two-day beta.{" "}
                  <Link to="/blog/deepseek-v41-flash-generally-available" className="text-primary hover:underline">
                    Read the GA release coverage
                  </Link>
                  .
                </p>
              </CardContent>
            </Card>



            <Card className="mb-8 border-blue-200 bg-blue-50/60">
              <CardContent className="p-6">
                <h2 className="text-lg font-bold text-slate-900 mb-3 mt-0">The 60-second version</h2>
                <ul className="text-sm text-slate-800 space-y-2 list-disc pl-5">
                  <li>
                    <strong>Model ID:</strong> <code>deepseek-v4.1-flash-expires-on-0910</code>. The
                    base URL does not change — swap the model string only.
                  </li>
                  <li>
                    <strong>New architecture with native multimodal support</strong>, per DeepSeek's
                    own description: images and text handled inside one model instead of via a
                    separate vision extension.
                  </li>
                  <li>
                    <strong>Billing matched <code>deepseek-v4-flash</code></strong> during the test —
                    the flat rates in force in September 2026: $0.14 / 1M input (cache miss), $0.0028
                    on a cache hit, $0.28 / 1M output. Those rates were replaced at the GA release; see{" "}
                    <Link to="/pricing" className="text-primary hover:underline">current pricing</Link>.
                  </li>
                  <li>
                    <strong>20 concurrent requests per account.</strong> That is the hard ceiling for
                    any benchmark you run.
                  </li>
                  <li>
                    <strong>Reported throughput up to 500 tokens/second</strong> (September 9
                    follow-up coverage), described as the largest architecture change on the Flash
                    line so far. Unverified — no first-party number has been published.
                  </li>
                  <li>
                    <strong>Not a launch.</strong> No model card, no technical report, no changelog
                    entry. As of September 9, 2026 the official change log still ends at the August
                    21 V4-Flash-Vision-Exp release, and the public Models &amp; Pricing page lists
                    only V4 Flash, V4 Pro and V4-Flash-Vision-Exp.
                  </li>
                </ul>
              </CardContent>
            </Card>



            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              What DeepSeek actually claimed
            </h2>
            <p className="text-slate-700 mb-4">
              The official wording is short. V4.1 Flash is an intermediate checkpoint that uses a{" "}
              <strong>new model architecture with native multimodal support</strong>, and DeepSeek
              claims it is <strong>stronger, faster and cheaper</strong> than V4 Flash. That is a
              vendor claim in a community-group post accompanied by a feedback form, not a benchmark
              table — there are no published scores to check it against yet.
            </p>
            <p className="text-slate-700 mb-8">
              Note what is <em>not</em> claimed: nothing about V4 Pro, nothing about the consumer app,
              and no new price. If you were waiting for the official V4-Pro release promised{" "}
              <Link to="/blog/deepseek-v4-flash-ga-agent-benchmarks" className="text-[#0066FF] underline">
                when V4-Flash went GA on July 31
              </Link>
              , this is not it.
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              Why "native multimodal" is the real change
            </h2>
            <p className="text-slate-700 mb-4">
              Until now, mixing images and text on the Flash line meant reaching for a separate vision
              model —{" "}
              <Link to="/blog/deepseek-vision-api-guide" className="text-[#0066FF] underline">
                <code>deepseek-v4-flash-vision-exp</code>
              </Link>
              . That approach runs the image through its own encoder and splices the result into the
              text sequence. It works, but the conversion step costs latency and loses information at
              the seam.
            </p>
            <p className="text-slate-700 mb-8">
              Native multimodality means images and text are trained and served inside the same
              architecture. On a product line whose entire reason to exist is low latency, that is a
              directional decision rather than a feature bullet: it suggests the cheap tier, not the
              premium tier, is where DeepSeek wants vision to live. If it holds, "add an image to your
              existing Flash call" becomes the default rather than an experimental detour.
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              How to test it before it disappears
            </h2>
            <div className="bg-slate-900 rounded-lg p-5 mb-6 overflow-x-auto">
              <pre className="text-sm text-slate-100"><code>{`from openai import OpenAI

client = OpenAI(
    api_key="YOUR_DEEPSEEK_API_KEY",
    base_url="https://api.deepseek.com",
)

resp = client.chat.completions.create(
    model="deepseek-v4.1-flash-expires-on-0910",  # expires 2026-09-10
    messages=[{"role": "user", "content": "Summarise this in one line."}],
)
print(resp.choices[0].message.content)`}</code></pre>
            </div>
            <p className="text-slate-700 mb-4">
              Three things worth measuring while the window is open, because nobody has published them
              yet:
            </p>
            <ul className="text-slate-700 space-y-2 list-disc pl-6 mb-8">
              <li>
                <strong>Time to first token vs <code>deepseek-v4-flash</code></strong> on identical
                prompts, single-threaded, so the 20-request cap cannot distort the result.
              </li>
              <li>
                <strong>Image-token accounting.</strong> Vision-Exp bills images at a capped token
                budget; check what the new architecture reports in <code>usage</code> for the same
                image.
              </li>
              <li>
                <strong>Cache-hit behaviour</strong> on repeated prefixes — cache pricing is where the
                Flash tier gets genuinely cheap, and a new architecture can change hit rates.
              </li>
            </ul>

            <Card className="mb-8 border-amber-200 bg-amber-50/60">
              <CardContent className="p-6">
                <h2 className="text-lg font-bold text-slate-900 mb-3 mt-0 flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-600" />
                  Do not pin this in production
                </h2>
                <p className="text-sm text-slate-800">
                  The expiry is inside the model ID. Any deployment calling{" "}
                  <code>deepseek-v4.1-flash-expires-on-0910</code> after September 10, 2026 fails on
                  an invalid model name — not gracefully, and not with a fallback. Keep production on{" "}
                  <code>deepseek-v4-flash</code>, which DeepSeek keeps pointed at the newest stable
                  build, and gate the beta ID behind an environment flag in a test path only.
                </p>
              </CardContent>
            </Card>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              What it signals about pricing
            </h2>
            <p className="text-slate-700 mb-4">
              Billing during the beta matched V4 Flash exactly, so nothing changed on invoices at the
              time. But "lower cost" was one of the three claims, and DeepSeek has form here: the{" "}
              <Link to="/blog/deepseek-v4-pro-api-price-cut-permanent" className="text-[#0066FF] underline">
                V4-Pro rate cut
              </Link>{" "}
              became permanent, and the long-announced peak-hour surcharge went live with the GA
              release on September 9, 2026 — off-peak rates are now half of peak. Our{" "}
              <Link to="/pricing" className="text-[#0066FF] underline">
                DeepSeek pricing page
              </Link>{" "}
              tracks the official rate card and is checked weekly.
            </p>
            <p className="text-slate-700 mb-8">
              The read at the time: an architecture preview, priced as its predecessor to remove any
              reason not to test it. The rate card that followed two days later confirmed the new
              tiering.
            </p>


            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              Frequently asked questions
            </h2>
            <div className="space-y-5 mb-10">
              {faqSchema.mainEntity.map((q) => (
                <div key={q.name}>
                  <h3 className="font-semibold text-slate-900 mb-1 flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-[#0066FF] mt-1 shrink-0" />
                    {q.name}
                  </h3>
                  <p className="text-slate-700 text-sm pl-6">{q.acceptedAnswer.text}</p>
                </div>
              ))}
            </div>

            <div className="border-t border-slate-200 pt-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Keep reading</h2>
              <RelatedLinks />
            </div>
          </article>
        </main>
      </div>
    </>
  );
};

export default DeepseekV41FlashBeta;
